import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, HStack, Icon, Link, Text, useToast } from '@chakra-ui/react';
import { LuBellOff, LuBellRing, LuTriangleAlert } from 'react-icons/lu';
import { useNotificationStatus } from '../hooks/useNotificationStatus';
import { registerVapidSubscription } from '../push/registerVapidSubscription';

export function buildNotificationStatusCopy(state) {
  switch (state) {
    case 'unsupported':
      return {
        icon: LuTriangleAlert,
        gradient: 'linear(to-r, red.500, orange.400)',
        textColor: 'whiteAlpha.900',
        title: 'Notifications unsupported',
        body: (
          <>
            This browser cannot send push notifications for Ekkos. Please install the app for push notifications!
          </>
        ),
        showRetry: false,
      };
    case 'noSW':
      return {
        icon: LuTriangleAlert,
        gradient: 'linear(to-r, orange.400, yellow.300)',
        textColor: 'gray.900',
        title: 'Enable service workers',
        body: (
          <>
            Service workers are disabled or unavailable, so notifications will not arrive. Ensure they are allowed and reload.
          </>
        ),
        showRetry: false,
      };
    case 'denied':
      return {
        icon: LuBellOff,
        gradient: 'linear(to-r, orange.400, orange.200)',
        textColor: 'gray.900',
        title: 'Notifications blocked',
        body: (
          <>
            Ekkos needs notifications to work. Update your browser settings to allow them, then retry below.
          </>
        ),
        showRetry: true,
      };
    case 'default':
      return {
        icon: LuBellRing,
        gradient: 'linear(to-r, blue.400, blue.300)',
        textColor: 'whiteAlpha.900',
        title: 'Turn on notifications',
        body: (
          <>
            Ekkos needs notifications to keep your reminders on time. Enable them now so we can alert you reliably.
          </>
        ),
        showRetry: true,
      };
    case 'granted':
    default:
      return {
        icon: LuBellRing,
        gradient: '',
        textColor: '',
        title: '',
        body: null,
        showRetry: false,
      };
  }
}

export default function NotificationsRequiredBanner() {
  const status = useNotificationStatus();
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const config = useMemo(() => buildNotificationStatusCopy(status), [status]);

  const handleRetry = useCallback(async () => {
    if (status === 'unsupported' || status === 'noSW') return;
    if (typeof Notification === 'undefined') return;

    try {
      setLoading(true);
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast({
          title: 'Notifications still blocked',
          description: 'Please enable notifications in your browser settings and try again.',
          status: 'warning',
        });
        return;
      }

      await registerVapidSubscription();
      toast({
        title: 'Notifications enabled',
        status: 'success',
      });
    } catch (error) {
      console.error('Failed to register VAPID subscription', error);
      toast({
        title: 'Could not enable notifications',
        description: 'Please try again or check your connection.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [status, toast]);

  if (status === 'granted') return null;

  return (
    <Box
      w="100%"
      bgGradient={config.gradient}
      color={config.textColor}
      p={3}
      borderRadius="md"
      mb={4}
      role="alert"
      aria-live="assertive"
    >
      <HStack justifyContent="space-between" alignItems="center">
        <HStack spacing={3} alignItems="flex-start">
          <Icon as={config.icon} boxSize={6} mt={0.5} />
          <Box>
            <Text fontWeight="bold" fontSize="md">
              {config.title}
            </Text>
            <Text fontSize="sm">
              {config.body}
              {(status === 'unsupported' || status === 'noSW') && (
                <>
                  {' '}
                  <Link href="https://support.google.com/chrome/answer/3220216" isExternal color="inherit" textDecoration="underline">
                    Learn more
                  </Link>
                </>
              )}
            </Text>
          </Box>
        </HStack>
        {config.showRetry && (
          <Button
            size="sm"
            variant="solid"
            colorScheme={status === 'default' ? 'blue' : 'orange'}
            onClick={handleRetry}
            isLoading={loading}
          >
            Retry
          </Button>
        )}
      </HStack>
    </Box>
  );
}
